import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Search,
  Clear,
} from '@mui/icons-material';
import api from '../../services/api';
import { Card } from '../../types';
import CardsTable from '../../components/cards/CardsTable';

const SearchCardsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setError('Please enter a search term');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use the correct endpoint format with path parameter
      const response = await api.get<Card[]>(`/search-cards-rbac/${encodeURIComponent(searchTerm)}`);
      setCards(response.data);
      setSearched(true);
    } catch (error: any) {
      console.error('Search failed:', error);
      if (error.response?.status === 403) {
        setError('You do not have permission to search cards');
      } else if (error.response?.status === 404) {
        setCards([]);
        setSearched(true);
      } else {
        setError('Search failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setCards([]);
    setSearched(false);
    setError(null);
  };

  const handleRefresh = async () => {
    if (searchTerm.trim()) {
      await handleSearch();
    }
  };

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" sx={{ mb: 4 }}>
        Search Cards
      </Typography>

      <Paper 
        sx={{ 
          p: 3, 
          mb: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by surname..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClear} size="small">
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading || !searchTerm.trim()}
            sx={{
              minWidth: 120,
              background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
              boxShadow: '0 3px 5px 2px rgba(51, 136, 255, .3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #0066ff 30%, #3388ff 90%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 10px 4px rgba(51, 136, 255, .3)',
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Search'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary">
          Search across all cards you have access to by surname. The search is case-insensitive and will find partial matches.
        </Typography>
      </Paper>

      {searched && cards.length === 0 && (
        <Alert severity="info">
          No cards found matching your search criteria.
        </Alert>
      )}

      {cards.length > 0 && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Found {cards.length} card{cards.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          <CardsTable 
            cards={cards} 
            onRefresh={handleRefresh}
            showClubColumn={true}
          />
        </>
      )}
    </Container>
  );
};

export default SearchCardsPage;